import { Component, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <div class="topbar-spacer"></div>
    <header
      class="topbar"
      [class.topbar--scrolled]="scrolled"
      [class.topbar--menu-open]="mobileOpen"
    >
      <nav class="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <a routerLink="/" class="flex items-center gap-2 text-xl font-bold text-white">
          <img src="assets/logo.png" alt="Neonnexux logo" class="h-8 w-8 object-contain" />
          Neonnexux
        </a>

        <button
          class="lg:hidden text-white/80"
          (click)="toggleMobile()"
          aria-label="Toggle navigation menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <div class="hidden lg:flex items-center gap-8 text-sm font-medium text-white/70">
          <a routerLink="/" routerLinkActive="text-white" [routerLinkActiveOptions]="{exact:true}" class="hover:text-white transition-colors">Home</a>
          <a routerLink="/features" routerLinkActive="text-white" class="hover:text-white transition-colors">Features</a>
          <a routerLink="/how-it-works" routerLinkActive="text-white" class="hover:text-white transition-colors">How It Works</a>
          <a routerLink="/pricing" routerLinkActive="text-white" class="hover:text-white transition-colors">Pricing</a>
          <a routerLink="/ai-monitoring" routerLinkActive="text-white" class="hover:text-white transition-colors">AI Monitoring</a>
          <a routerLink="/blogs" routerLinkActive="text-white" class="hover:text-white transition-colors">Blogs</a>
          <a routerLink="/about" routerLinkActive="text-white" class="hover:text-white transition-colors">About Us</a>
          <a routerLink="/contact" routerLinkActive="text-white" class="hover:text-white transition-colors">Contact</a>
        </div>

        <div class="hidden lg:flex items-center gap-3">
          @if (auth.currentUser(); as user) {
            <a routerLink="/dashboard/overview" class="btn-secondary !px-4 !py-2 text-sm">Dashboard</a>
            <button (click)="auth.logout()" class="btn-primary !px-4 !py-2 text-sm">Logout</button>
          } @else {
            <a routerLink="/login" class="btn-secondary !px-4 !py-2 text-sm">Login</a>
            <a routerLink="/signup" class="btn-primary !px-4 !py-2 text-sm">Get Started</a>
          }
        </div>
      </nav>

      @if (mobileOpen) {
        <div class="lg:hidden flex flex-col gap-1 px-6 pb-4 text-white/80 text-sm">
          <a routerLink="/" (click)="mobileOpen = false" class="py-2 border-b border-white/5">Home</a>
          <a routerLink="/features" (click)="mobileOpen = false" class="py-2 border-b border-white/5">Features</a>
          <a routerLink="/how-it-works" (click)="mobileOpen = false" class="py-2 border-b border-white/5">How It Works</a>
          <a routerLink="/pricing" (click)="mobileOpen = false" class="py-2 border-b border-white/5">Pricing</a>
          <a routerLink="/ai-monitoring" (click)="mobileOpen = false" class="py-2 border-b border-white/5">AI Monitoring</a>
          <a routerLink="/blogs" (click)="mobileOpen = false" class="py-2 border-b border-white/5">Blogs</a>
          <a routerLink="/about" (click)="mobileOpen = false" class="py-2 border-b border-white/5">About Us</a>
          <a routerLink="/contact" (click)="mobileOpen = false" class="py-2 border-b border-white/5">Contact</a>
          <div class="flex gap-3 pt-3">
            @if (auth.currentUser()) {
              <a routerLink="/dashboard/overview" class="btn-secondary !px-4 !py-2 text-sm flex-1 text-center">Dashboard</a>
            } @else {
              <a routerLink="/login" class="btn-secondary !px-4 !py-2 text-sm flex-1 text-center">Login</a>
              <a routerLink="/signup" class="btn-primary !px-4 !py-2 text-sm flex-1 text-center">Get Started</a>
            }
          </div>
        </div>
      }
    </header>
  `,
  styles: [`
    :host { display: block; }
    .topbar-spacer { height: 72px; }
    /* Always fixed — never scrolls away, no matter what */
    .topbar {
      position: fixed;
      top: 0; left: 0; right: 0;
      z-index: 50;
      background: rgba(5, 7, 15, 0.85);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 0;
      transition: top .35s cubic-bezier(.4, 0, .2, 1), left .35s cubic-bezier(.4, 0, .2, 1), right .35s cubic-bezier(.4, 0, .2, 1), border-radius .35s cubic-bezier(.4, 0, .2, 1), background .35s ease, box-shadow .35s ease, backdrop-filter .35s ease;
      will-change: top, left, right, border-radius;
    }
    /* After crossing the first div/height -> floating blurry pill (circle left + right), NOT fully black */
    .topbar--scrolled {
      top: 12px;
      left: 12px;
      right: 12px;
      border-radius: 9999px;
      background: rgba(5, 7, 15, 0.55);
      -webkit-backdrop-filter: blur(20px) saturate(1.4);
      backdrop-filter: blur(20px) saturate(1.4);
      border: 1px solid rgba(255, 255, 255, 0.12);
      box-shadow: 0 12px 40px -12px rgba(0, 0, 0, 0.55);
    }
    @media (min-width: 1024px) {
      .topbar--scrolled { left: 24px; right: 24px; max-width: 80rem; margin-inline: auto; }
    }
    /* Mobile menu open -> keep pill sides but expand bottom so links fit */
    .topbar--menu-open {
      border-radius: 24px;
    }
    .topbar--menu-open.topbar--scrolled { border-radius: 24px; }
    @media (prefers-reduced-motion: reduce) {
      .topbar { transition: none; }
    }
  `],
})
export class NavbarComponent implements OnDestroy {
  mobileOpen = false;

  /**
   * True once the page is scrolled past the first section (~hero).
   * At the top the bar is a full-width rectangle; after crossing the
   * threshold it becomes a floating blurry pill (circle on left/right).
   */
  scrolled = false;

  private routerSub: Subscription;

  constructor(public auth: AuthService, private router: Router) {
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        // Keep the bar fixed on every route — just re-evaluate the pill state on nav.
        this.updateScrolled();
      });
    // Set initial state (page could load mid-scroll after refresh)
    this.updateScrolled();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateScrolled();
  }

  private updateScrolled(): void {
    // Becomes reactive once it crosses the first div / ~hero height — not fully black, blurry pill.
    const y = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    this.scrolled = y > 120;
  }

  /** Opening the mobile menu always brings the bar back into view. */
  toggleMobile(): void {
    this.mobileOpen = !this.mobileOpen;
  }

  ngOnDestroy(): void {
    this.routerSub.unsubscribe();
  }
}
